export function safeParseObject(value: any): any {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return {};
}

export function normalizeQuestionOptions(question: any): any[] {
  const metadata = safeParseObject(question?.metadata);
  const content = safeParseObject(question?.content);
  const rawOptions = question?.options || question?.alternatives || question?.choices || metadata?.options || content?.options;

  if (!rawOptions || (Array.isArray(rawOptions) && rawOptions.length === 0)) {
    return ["A", "B", "C", "D"].map(letter => ({
      id: letter,
      label: letter,
      value: letter,
      text: question?.[`option${letter}`] || question?.[`option_${letter.toLowerCase()}`] || question?.[`option_${letter}`] || metadata?.[`option${letter}`] || "",
      image: getOptionImageUrl(question, letter),
      imageUrl: getOptionImageUrl(question, letter),
      audio: "",
    }));
  }

  let parsedOptions: any[] = [];

  if (Array.isArray(rawOptions)) {
    parsedOptions = rawOptions.map((option, index) => {
      const letter = String.fromCharCode(65 + index);
      if (typeof option === "string") {
        return {
          id: letter,
          label: letter,
          text: option,
          value: letter,
          image: getOptionImageUrl(question, letter),
          imageUrl: getOptionImageUrl(question, letter),
          audio: "",
        };
      }

      if (option && typeof option === "object") {
        const label =
          option.label ||
          option.id ||
          option.value ||
          letter;

        const img = option.image || option.imageUrl || getOptionImageUrl(question, label);
        return {
          ...option,
          id: option.id || label,
          label,
          value: option.value || label,
          text: option.text || option.content || option.label || "",
          image: img,
          imageUrl: img,
          audio: option.audio || option.audioUrl || "",
        };
      }

      return {
        id: letter,
        label: letter,
        text: "",
        value: letter,
        image: getOptionImageUrl(question, letter),
        imageUrl: getOptionImageUrl(question, letter),
        audio: "",
      };
    });
  } else if (rawOptions && typeof rawOptions === "object") {
    parsedOptions = Object.entries(rawOptions).map(([key, value]) => {
      const letter = String(key).toUpperCase();
      if (typeof value === "string") {
        return {
          id: key,
          label: key,
          value: key,
          text: value,
          image: getOptionImageUrl(question, letter),
          imageUrl: getOptionImageUrl(question, letter),
          audio: "",
        };
      }

      if (value && typeof value === "object") {
        const valObj = value as any;
        const img = valObj.image || valObj.imageUrl || getOptionImageUrl(question, letter);
        return {
          id: key,
          label: key,
          value: key,
          text: valObj.text || valObj.content || valObj.label || "",
          image: img,
          imageUrl: img,
          audio: valObj.audio || valObj.audioUrl || "",
          ...valObj,
        };
      }

      return {
        id: key,
        label: key,
        value: key,
        text: "",
        image: getOptionImageUrl(question, letter),
        imageUrl: getOptionImageUrl(question, letter),
        audio: "",
      };
    });
  }

  // Final sweep to make sure every option has the image resolved if it doesn't already
  return parsedOptions.map((opt: any) => {
    const label = String(opt.label || opt.id || opt.value || "").toUpperCase();
    const img = opt.image || opt.imageUrl || getOptionImageUrl(question, label);
    return {
      ...opt,
      image: img,
      imageUrl: img,
    };
  });
}


export function normalizeCompetency(value: any): "reading" | "listening" | "writing" | "" {
  const v = String(value || "").trim().toLowerCase();

  if (
    v === "listening" ||
    v === "co" ||
    v === "compréhension orale" ||
    v.includes("compréhension orale") ||
    v.includes("comprehension orale") ||
    v.includes("listening") ||
    v.includes("orale") ||
    v.includes("oral") ||
    /\bco\b/i.test(v)
  ) {
    return "listening";
  }

  if (
    v === "reading" ||
    v === "ce" ||
    v === "compréhension écrite" ||
    v.includes("compréhension écrite") ||
    v.includes("comprehension ecrite") ||
    v.includes("reading") ||
    /\bce\b/i.test(v)
  ) {
    return "reading";
  }

  if (
    v === "writing" ||
    v === "ee" ||
    v === "expression écrite" ||
    v.includes("expression écrite") ||
    v.includes("expression ecrite") ||
    v.includes("writing") ||
    /\bee\b/i.test(v)
  ) {
    return "writing";
  }

  return "";
}

export function normalizeQuestionCompetency(value: any): "Reading" | "Listening" | "Writing" {
  const v = String(value || "").trim().toLowerCase();

  if (
    v === "listening" ||
    v === "co" ||
    v.includes("compréhension orale") ||
    v.includes("comprehension orale") ||
    v.includes("listening") ||
    v.includes("orale") ||
    v.includes("oral")
  ) {
    return "Listening";
  }

  if (
    v === "writing" ||
    v === "ee" ||
    v.includes("expression écrite") ||
    v.includes("expression ecrite") ||
    v.includes("writing")
  ) {
    return "Writing";
  }

  return "Reading";
}

export function getQuestionAudioUrl(question: any): string {
  const metadata = safeParseObject(question?.metadata);
  const media = safeParseObject(question?.media);
  const content = safeParseObject(question?.content);

  return (
    question?.audioUrl ||
    question?.audio_url ||
    question?.audio ||
    media?.audioUrl ||
    media?.audio_url ||
    media?.audio ||
    metadata?.audioUrl ||
    metadata?.audio_url ||
    metadata?.audio ||
    content?.audioUrl ||
    content?.audio_url ||
    content?.audio ||
    metadata?.media?.audioUrl ||
    metadata?.media?.audio ||
    ""
  );
}

export function getOptionImageUrl(question: any, label: string): string {
  const upper = String(label || "").toUpperCase();

  const metadata = safeParseObject(question?.metadata);
  const media = safeParseObject(question?.media);
  const content = safeParseObject(question?.content);

  const direct =
    question?.[`option${upper}Image`] ||
    question?.[`option_${upper.toLowerCase()}_image`] ||
    question?.[`option_${upper}_image`] ||
    question?.[`image${upper}`] ||
    question?.[`image_${upper.toLowerCase()}`] ||
    question?.[`image_${upper}`] ||
    "";

  if (direct) return direct;

  const metadataDirect =
    metadata?.[`option${upper}Image`] ||
    metadata?.[`option_${upper.toLowerCase()}_image`] ||
    metadata?.[`image${upper}`] ||
    metadata?.optionImages?.[upper] ||
    metadata?.option_images?.[upper] ||
    metadata?.images?.[upper] ||
    metadata?.media?.optionImages?.[upper] ||
    metadata?.media?.images?.[upper] ||
    "";

  if (metadataDirect) return metadataDirect;

  const mediaDirect =
    media?.[`option${upper}Image`] ||
    media?.optionImages?.[upper] ||
    media?.option_images?.[upper] ||
    media?.images?.[upper] ||
    "";

  if (mediaDirect) return mediaDirect;

  const contentDirect =
    content?.[`option${upper}Image`] ||
    content?.optionImages?.[upper] ||
    content?.option_images?.[upper] ||
    content?.images?.[upper] ||
    "";

  if (contentDirect) return contentDirect;

  const rawOptions =
    Array.isArray(question?.options)
      ? question.options
      : Array.isArray(metadata?.options)
        ? metadata.options
        : Array.isArray(content?.options)
          ? content.options
          : [];

  const option = rawOptions.find((opt: any) => {
    const optLabel = String(opt?.label || opt?.id || opt?.value || opt?.letter || "").toUpperCase();
    return optLabel === upper;
  });

  return (
    option?.image ||
    option?.imageUrl ||
    option?.image_url ||
    option?.optionImage ||
    option?.media?.image ||
    option?.metadata?.image ||
    ""
  );
}

export function getQuestionOptionImages(question: any) {
  return {
    A: getOptionImageUrl(question, "A"),
    B: getOptionImageUrl(question, "B"),
    C: getOptionImageUrl(question, "C"),
    D: getOptionImageUrl(question, "D"),
  };
}

export function buildQuestionImportFingerprint(question: any): string {
  if (!question) return "";

  const normalizeListeningSectionLocal = (sectionName: string): string => {
    const s = String(sectionName || "").trim().toLowerCase();
    if (
      s.includes("dialogues associés") ||
      s.includes("dialogues associes") ||
      s.includes("images") ||
      s === "section a" ||
      s === "dialogues_images"
    ) {
      return "Dialogues associés à des images";
    }
    if (s.includes("messages vocaux") || s === "section b" || s === "messages_vocaux") {
      return "Messages vocaux";
    }
    if (s.includes("annonces publiques") || s === "section c" || s === "annonces_publiques") {
      return "Annonces publiques";
    }
    if (s.includes("reportages courts") || s === "section d" || s === "dialogues_reportages_courts") {
      return "Dialogues / Reportages courts";
    }
    if (s.includes("conférences longues") || s.includes("conferences longues") || s === "section e" || s === "discussions_conferences_longues") {
      return "Discussions / Conférences longues";
    }
    return sectionName || "Messages vocaux";
  };

  const parts = [
    normalizeQuestionCompetency(question.competency || question.competence),
    normalizeListeningSectionLocal(question.section || ""),
    String(question.title || "").trim().toLowerCase(),
    String(question.question || question.questionText || question.prompt || "").trim().toLowerCase(),
    String(question.instruction || "").trim().toLowerCase(),
    String(question.correctAnswer || "").trim().toUpperCase(),
    String(question.audioFileName || question.audioUrl || question.audio || "").trim().toLowerCase(),
    String(question.optionAImage || getOptionImageUrl(question, "A") || "").trim().toLowerCase(),
    String(question.optionBImage || getOptionImageUrl(question, "B") || "").trim().toLowerCase(),
    String(question.optionCImage || getOptionImageUrl(question, "C") || "").trim().toLowerCase(),
    String(question.optionDImage || getOptionImageUrl(question, "D") || "").trim().toLowerCase(),
  ];

  return parts.join("|");
}

export function sortQuestionsByStableImportOrder(questions: any[]): any[] {
  return [...questions].sort((a, b) => {
    const aOrder = Number(a.importOrder || a.questionNumber || a.originalImportIndex + 1 || 9999);
    const bOrder = Number(b.importOrder || b.questionNumber || b.originalImportIndex + 1 || 9999);
    return aOrder - bOrder;
  });
}


export function normalizeImportedQuestion(question: any): any {
  if (!question) return question;
  
  const normalizedOptions = normalizeQuestionOptions(question);

  const normalizedQuestion = {
    ...question,
    options: normalizedOptions,
    correctAnswer: String(
      question.correctAnswer ||
      question.answer ||
      question.correct ||
      question.correctOption ||
      ""
    ).trim().toUpperCase(),
    question:
      question.question ||
      question.questionText ||
      question.prompt ||
      "",
    instruction:
      question.instruction ||
      question.instructions ||
      "",
    title:
      question.title ||
      question.name ||
      "",
    section:
      question.section ||
      "",
    competency:
      question.competency ||
      "",
  };

  return normalizedQuestion;
}

export function normalizeImportedQuestionsPayload(payload: any): any {
  if (!payload) return payload;

  const payloadCompetency = normalizeCompetency(payload?.competency || payload?.competence);

  if (Array.isArray(payload)) {
    return payload.map(q => {
      const normQ = normalizeImportedQuestion(q);
      return {
        ...normQ,
        competency: normalizeCompetency(normQ.competency || q.competency || q.competence) || payloadCompetency || ""
      };
    });
  }

  if (Array.isArray(payload?.questions)) {
    const normalizedQuestions = payload.questions.map((q: any) => {
      const normQ = normalizeImportedQuestion(q);
      return {
        ...normQ,
        competency: normalizeCompetency(normQ.competency || q.competency || q.competence) || payloadCompetency || ""
      };
    });
    return {
      ...payload,
      competency: payloadCompetency || payload.competency || "",
      questions: normalizedQuestions,
    };
  }

  if (payload && typeof payload === "object") {
    const normQ = normalizeImportedQuestion(payload);
    return {
      ...normQ,
      competency: normalizeCompetency(normQ.competency || payload.competency || payload.competence) || payloadCompetency || ""
    };
  }

  return payload;
}

